import { io, Socket } from 'socket.io-client';

class WebSocketService {
  private socket: Socket | null = null;
  private roomId: string | null = null;
  private lastJoin: { roomId: string; playerName: string; isAdmin: boolean; playerId?: string } | null = null;

  connect() {
    // Don't create multiple connections
    if (this.socket && this.socket.connected) {
      console.log('Socket already connected, reusing existing connection');
      return this.socket;
    }
    
    // Socket exists but lost connection – reconnect it in-place so all
    // game event listeners (onPlayersUpdated, onVotingUpdated, etc.)
    // registered by PlayGame's useEffect are preserved.
    if (this.socket) {
      console.log('Reconnecting existing socket (preserving listeners)...');
      this.socket.connect();
      return this.socket;
    }
    
    // Decide which URL to use for the Socket.IO connection.  
    // – In production we still want to connect to the **same origin** that served the page,  
    //   so we *don't* pass a URL (behaviour unchanged).  
    // – In local development, however, the React/Vite dev-server usually runs on another
    //   port (e.g. 3001) while the backend API & Socket.IO server run on 3000. Connecting
    //   to the page origin therefore hits the wrong port. In that case we explicitly
    //   connect to "http://localhost:3000".

    const isLocalhost = [
      'localhost',
      '127.0.0.1',
      '::1', // IPv6 localhost
    ].includes(window.location.hostname);

    const socketUrl = isLocalhost ? 'http://localhost:3000' : undefined;

    console.log(
      `Attempting to connect WebSocket to ${socketUrl ?? 'current page origin'}...`,
    );

    // If socketUrl is undefined we deliberately rely on Socket.IO's default behaviour
    // (connect to the page's origin). Otherwise we pass the explicit URL.
    this.socket = socketUrl
      ? io(socketUrl, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        })
      : io({
          withCredentials: true,
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });

    this.socket.on('connect', () => {
        console.log('Successfully connected to WebSocket server!');
        // Auto rejoin the last room after (re)connect
        if (this.lastJoin) {
          this.socket!.emit('join-room', this.lastJoin);
        }
    });

    // Extra safety for some environments where reconnect is fired separately
    this.socket.on('reconnect', () => {
        console.log('Reconnected to WebSocket server');
        if (this.lastJoin) {
          this.socket!.emit('join-room', this.lastJoin);
        }
    });

    this.socket.on('disconnect', () => {
        console.log('Disconnected from WebSocket server');
    });

    this.socket.on('error', (error: any) => {
        console.error('WebSocket connection error:', error);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.roomId = null;
      this.lastJoin = null; // prevent unintended auto-rejoin after explicit disconnect
    }
  }

  joinRoom(roomId: string, playerName: string, isAdmin: boolean, playerId?: string) {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    
    this.roomId = roomId;
    this.lastJoin = { roomId, playerName, isAdmin, playerId };
    this.socket.emit('join-room', this.lastJoin);
  }

  // Event emitters
  changeDeck(deck: string) {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('change-deck', { roomId: this.roomId, deck });
  }

  updateIcon(playerId: string, iconId: number) {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('update-icon', { roomId: this.roomId, playerId, iconId });
  }

  startGame() {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('start-game', { roomId: this.roomId });
  }

  // Voting: emit a vote for a target player
  castVote(voterId: string, targetId: string) {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('cast-vote', { roomId: this.roomId, voterId, targetId });
  }

  // REMOVED: endRound() - Now handled by REST API POST /api/rooms/:roomId/end-round
  // REMOVED: restartGame() - Now handled by REST API POST /api/rooms/:roomId/restart

  endGame() {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('end-game', { roomId: this.roomId });
  }

  // REMOVED: updateScores() - Now handled by REST API POST /api/rooms/:roomId/game-round-end

  // Event listeners
  onPlayersUpdated(callback: (data?: { players: any[]; currentDeck?: string }) => void) {
    if (!this.socket) return;
    this.socket.on('players-updated', callback);
  }

  onDeckUpdated(callback: (data: { deck: string }) => void) {
    if (!this.socket) return;
    this.socket.on('deck-updated', callback);
  }

  onPlayerIconUpdated(callback: (data: { playerId: string; iconId: number }) => void) {
    if (!this.socket) return;
    this.socket.on('player-icon-updated', callback);
  }

  onGameStarted(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('game-started', callback);
  }

  onRoundEnded(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('round-ended', callback);
  }

  onGameEnded(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('game-ended', callback);
  }

  onGameRestarted(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('game-restarted', callback);
  }

  onScoresUpdated(callback: (data?: { outcome?: 'spy_escaped' | 'spy_caught_location' | 'spy_caught' }) => void) {
    if (!this.socket) return;
    this.socket.on('scores-updated', callback);
  }

  // Voting listeners
  onVotingUpdated(callback: (data: { counts: Record<string, number>; voters: string[]; isClosed?: boolean }) => void) {
    if (!this.socket) return;
    this.socket.on('voting-updated', callback);
  }

  onVotingComplete(callback: (data: { accusedId: string | null; isSpy: boolean; isUnanimous: boolean; spyGuessDeadline?: number }) => void) {
    if (!this.socket) return;
    this.socket.on('voting-complete', callback);
  }

  requestVotingState() {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('request-voting-state', { roomId: this.roomId });
  }

  endVoting() {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('end-voting', { roomId: this.roomId });
  }

  submitSpyGuess(playerId: string, guessedLocation: string) {
    if (!this.socket || !this.roomId) return;
    this.socket.emit('spy-guess', { roomId: this.roomId, playerId, guessedLocation });
  }

  onSpyGuessResult(callback: (data: { correct: boolean; guessedLocation: string | null; actualLocation: string }) => void) {
    if (!this.socket) return;
    this.socket.on('spy-guess-result', callback);
  }

  onRoundActive(callback: (data: { roundStartedAt: number }) => void) {
    if (!this.socket) return;
    this.socket.on('round-active', callback);
  }

  onSpyGuessActive(callback: (data: { spyGuessDeadline: number }) => void) {
    if (!this.socket) return;
    this.socket.on('spy-guess-active', callback);
  }

  onVotingEndedManually(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('voting-ended-manually', callback);
  }

  onError(callback: (error: { message: string }) => void) {
    if (!this.socket) return;
    this.socket.on('error', callback);
  }

  onRoomDeleted(callback: () => void) {
    if (!this.socket) return;
    this.socket.on('room-deleted', callback);
  }

  // Remove specific listeners
  removeListener(event: string, callback?: (...args: any[]) => void) {
    if (!this.socket) return;
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  // Cleanup all listeners
  removeAllListeners() {
    if (!this.socket) return;
    this.socket.removeAllListeners();
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Attempt to reconnect and rejoin room proactively (e.g., on tab focus)
  resume() {
    this.connect();
    if (this.socket && this.lastJoin) {
      this.socket.emit('join-room', this.lastJoin);
    }
  }
}

export const socketService = new WebSocketService(); 